import moment from 'moment'
import debounce from 'lodash.debounce'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import classnames from 'classnames'
import { Row, Col } from 'react-flexbox-grid'
import { reduxForm, formValueSelector, initialize, change } from 'redux-form'
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl'
import { Button } from 'components/util'
import injectUnits from 'shared/units/injectUnits'
import { selectActivityTypes } from 'services/activityTypes/selectors'
import { selectUnitPreference } from 'services/user/selectors'
import { selectLanguageCode } from 'services/bootup/selectors'
import * as workouts from 'services/workouts/selectors'
import { fetchCalories } from 'services/workouts/actions'
import * as helpers from 'services/workouts/helpers'
import Common from './Common'
import ActivitySelector from './ActivitySelector'
import Distance from './Distance'
import Duration from './Duration'
import CalorieCalculator from './CalorieCalculator'
import HeartRate from './HeartRate'
import RouteSelector from 'components/routes/RouteSelector'
import Social from './Social'
import Speed from './Speed'
import Pace from './Pace'
import Swim from './Swim'
import Steps from './Steps'
import validate from './validate'
import styles from './_workoutForm.css'
import convert from 'shared/units/convert'
import {
  METERS,
  IMPERIAL,
  MILES,
  KILOMETERS,
  MILES_PER_HOUR,
  KILOMETERS_PER_HOUR,
  SECOND_MI,
  SECOND_KM,
  SECONDS,
  HOURS,
} from 'shared/units/constants'

const FORM_NAME = 'workoutForm'

const formOptions = {
  form: FORM_NAME,
  initialValues: {
    startDate: moment(),
    poolLengthUnit: METERS,
    mode: 'create',
  },
  validate,
}

const valueSelector = formValueSelector(FORM_NAME)

const mapStateToProps = state => ({
  state: state,
  saveError: workouts.selectError(state),
  locale: selectLanguageCode(state),
  unitPreference: selectUnitPreference(state),
  start: valueSelector(state, 'startDateTime'),
  route: valueSelector(state, 'route'),
  distance: valueSelector(state, 'distance'),
  activityType: valueSelector(state, 'activityType'),
  duration: valueSelector(state, 'duration'),
  laps: valueSelector(state, 'laps'),
  poolLength: valueSelector(state, 'poolLength'),
  poolLengthUnit: valueSelector(state, 'poolLengthUnit'),
  pace: valueSelector(state, 'pace'),
  activityTypes: selectActivityTypes(state),
  selectedActivity: workouts.selectChosenActivity(state),
  showPaceInput: workouts.selectShowPaceInput(state),
  showSpeedInput: workouts.selectShowSpeedInput(state),
  showRouteInputs: workouts.selectShowRouteInputs(state),
  showDistanceInputs: workouts.selectShowDistanceInputs(state),
  showSwimInputs: workouts.selectShowSwimInputs(state),
  showStepsInput: workouts.selectShowStepsInput(state),
  updatedWorkout: workouts.selectApiDataFromForm(FORM_NAME)(state),
})

const mapDispatchToActions = {
  initialize,
  change,
  fetchCalories,
}

const messages = defineMessages({
  'distance_name': {
    id: 'workouts.create.distance_name',
    defaultMessage: '{distance} {activityName}',
  },
  'duration_name': {
    id: 'workouts.create.duration_name',
    defaultMessage: '{activityName} {hours}:{minutes}',
  },
})

export class WorkoutForm extends Component {

  static propTypes = {
    saveError: PropTypes.string,
    mode: PropTypes.oneOf(['create', 'edit']),
    pace: PropTypes.number,
    locale: PropTypes.string,
    state: PropTypes.object,
    name: PropTypes.string,
    start: PropTypes.object,
    intl: PropTypes.object,
    onSubmit: PropTypes.func,
    handleSubmit: PropTypes.func,
    initialize: PropTypes.func,
    change: PropTypes.func,
    route: PropTypes.object,
    selectedActivity: PropTypes.object,
    showSpeedInput: PropTypes.bool,
    showPaceInput: PropTypes.bool,
    showRouteInputs: PropTypes.bool,
    showDistanceInputs: PropTypes.bool,
    showStepsInput: PropTypes.bool,
    showSwimInputs: PropTypes.bool,
    fetchUserGear: PropTypes.func,
    userGearOptions: PropTypes.array,
    distance: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    duration: PropTypes.number,
    durationHH: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    durationMM: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    durationSS: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    activityType: PropTypes.string,
    activityTypes: PropTypes.array,
    formatDistance: PropTypes.func,
    unitPreference: PropTypes.string,
    poolLengthUnit: PropTypes.string,
    poolLength: PropTypes.string,
    laps: PropTypes.string,
    fetchCalories: PropTypes.func,
    className: PropTypes.string,
    defaultWorkout: PropTypes.object,
    defaultDate: PropTypes.object,
    updatedWorkout: PropTypes.object,
  }

  componentWillMount () {
    const { mode, defaultWorkout, defaultDate } = this.props

    if (defaultWorkout) {
      this.props.initialize({
        ...this.props.defaultWorkout,
        poolLengthUnit: METERS,
        mode,
      })
    }

    if (defaultDate) {
      this.props.initialize({
        startDate: defaultDate,
        poolLengthUnit: METERS,
        mode,
      })
    }
  }

  handleRouteChange = (value) => {
    const { unitPreference: system } = this.props
    const distance = convert(value.distance)
      .from(METERS)
      .to(system === IMPERIAL ? MILES : KILOMETERS)
      .toFixed(2)

    this.props.change('route', value, true)
    this.props.change('distance', distance, true)
  }

  handleClearRoute = () => {
    this.props.change('route', null, true)
    this.props.change('distance', null, true)
  }

  getWorkoutName = (name, activity) => {
    const { intl, distance: _distance, durationHH, durationMM, unitPreference } = this.props
    const activityName = activity.name
    let workoutName = name || activityName

    if (name) { return name }

    if (_distance) {
      const distance = this.props.formatDistance({
        value: parseFloat(_distance),
        from: unitPreference === IMPERIAL ? MILES : KILOMETERS,
      })
      workoutName = intl.formatMessage(messages.distance_name, { activityName, distance })
    } else if (durationHH || durationMM) {
      const numberOptions = { minimumIntegerDigits: 2 }
      workoutName = intl.formatMessage(messages.duration_name, {
        activityName: activityName,
        hours: intl.formatNumber(parseInt(durationHH, 10) || 0, numberOptions),
        minutes: intl.formatNumber(parseInt(durationMM, 10) || 0, numberOptions),
      })
    }

    return workoutName
  }

  handleSubmit = () => {
    const { updatedWorkout, activityTypes, activityType: activityTypeId } = this.props
    const activityType = activityTypes.find(type => type._links.self[0].id === activityTypeId)
    const { name } = updatedWorkout
    const workoutName = this.getWorkoutName(name, activityType)
    const values = { ...updatedWorkout, name: workoutName }

    return this.props.onSubmit(values)
  }

  handleSwimChange = (name, value) => {
    const { poolLength, poolLengthUnit, laps, unitPreference: system } = this.props
    const currentSwimData = { poolLength, poolLengthUnit, laps, system }
    const swimData = { ...currentSwimData, [name]: value }
    const distance = helpers.getSwimDistance(swimData)

    if (distance) {
      this.props.change('distance', distance.toString(), true)
    }
  }

  updateSpeed = debounce((_distance, _duration) => {
    const { unitPreference: system, showPaceInput, showSpeedInput } = this.props
    const distance = parseFloat(_distance)
    const duration = parseInt(_duration, 10)

    if (isNaN(duration) || isNaN(distance)) { return }

    const hours = convert(duration).from(SECONDS).to(HOURS)
    const speed = hours > 0 ? (distance / hours) : 0

    if (showSpeedInput) {
      // if speed, just change with speed value directly...easy peezy
      this.props.change('speed', speed.toFixed(2))
    } else if (showPaceInput) {
      // if pace, convert to seconds/mi or seconds/km first, then update
      const to = system === IMPERIAL ? SECOND_MI : SECOND_KM
      const from = system === IMPERIAL ? MILES_PER_HOUR : KILOMETERS_PER_HOUR
      const pace = speed <= 0 ? 0 : convert(speed).from(from).to(to)
      this.props.change('pace', pace)
    }
  }, 250)

  handleDurationChange = (duration) => {
    const { distance } = this.props
    this.updateSpeed(distance, duration)
  }

  handleDistanceChange = ({ target: { value: distance }}) => {
    const { duration } = this.props
    this.updateSpeed(distance, duration)
  }

  handleActivityChange = () => {
    const { duration, distance } = this.props
    this.updateSpeed(distance, duration)
  }

  render () {
    const {
      handleSubmit,
      selectedActivity,
      showStepsInput,
      showSwimInputs,
      showSpeedInput,
      showPaceInput,
      showRouteInputs,
      showDistanceInputs,
      userGearOptions,
      route,
      className,
      defaultWorkout,
      mode,
      saveError,
      pace,
    } = this.props

    return (
      <form
        className={classnames(styles.container, className)}
        onSubmit={handleSubmit(this.handleSubmit)}
      >

        <Common
          mode={mode}
          defaultWorkout={defaultWorkout}
          userGearOptions={userGearOptions}
        />

        <Row>
          <Col xs={12}>
            <ActivitySelector onChange={this.handleActivityChange} />
          </Col>
        </Row>

        {selectedActivity ?
          <Row>
            <Col xs={12}>

              {showRouteInputs ?
                <RouteSelector
                  route={route}
                  className={styles.routeSelector}
                  onChange={this.handleRouteChange}
                  onClearRoute={this.handleClearRoute}
                />
              : null}

              <Row>
                <Col xs={10} sm={8}>
                  <Duration
                    onChange={this.handleDurationChange}
                    formName={FORM_NAME}
                    defaultValue={defaultWorkout ? defaultWorkout.duration : null}
                  />
                </Col>
              </Row>

              {/* distance */}
              {showSwimInputs ? <Swim onChange={this.handleSwimChange} /> : null}
              {showDistanceInputs ? <Distance onChange={this.handleDistanceChange} /> : null}
              {showStepsInput ? <Steps /> : null}

              {/* bio */}
              <CalorieCalculator formName={FORM_NAME} />
              <HeartRate />

              {/* speed */}
              {showSpeedInput ? <Speed /> : null}
              {showPaceInput ?
                <Pace
                  formName={FORM_NAME}
                  defaultValue={pace ? pace : null}
                />
              : null}

            </Col>
          </Row>
        : null}

        <div className={styles.hr} />

        <Social className={styles.social} />

        {saveError ?
          <div className={styles.error}>
            {saveError === 'unknown_error' ?
              <FormattedMessage
                id='workouts.create.error.unknown'
                defaultMessage='Unknown error when saving workout.'
              />
            : null}

            {saveError === 'validation_error' ?
              <FormattedMessage
                id='workouts.create.error.validation'
                defaultMessage='Validation error when saving workout.'
              />
            : null}
          </div>
        : null}

        <Row>
          <Col xs={12} sm={3}>
            <div className={styles.saveButtonContainer}>
              <Button size='large' el='button' type='primary' className={styles.saveButton}>
                <FormattedMessage
                  id='workouts.create.save'
                  defaultMessage='Save'
                />
              </Button>
            </div>
          </Col>
        </Row>

      </form>
    )
  }
}

export const Form = reduxForm(formOptions)(WorkoutForm)
export const IntlPage = injectIntl(Form)
export const UnitsPage = injectUnits(IntlPage)
export default connect(mapStateToProps, mapDispatchToActions, null, { withRef: true })(UnitsPage)



// WEBPACK FOOTER //
// ./src/components/workouts/WorkoutForm/WorkoutForm.jsx